// Comps tab logic shared by today's pane (UnitDetailPane/CompsTab) and the
// full unit page (UnitV2/tabs/CompsTabV2), so the two can never drift: the
// market-comps fetch, the Evidence Explorer (year chips, hours bounds, states
// multi-select, sort, the 15-row cap), the price band statistics and verdict,
// the coverage states, the sold-comps period filter and the peer summaries.
// Every rule and label here is today's (parity file P-UNIT-095..115).

import {
    useCallback, useEffect, useRef, useState,
} from 'react';
import apiService, { isApiAbortError } from '../services/apiService';
import { formatNumber } from './formatting';

// Units the backend simply has no market coverage for. ONLY these reasons
// render nothing at all — a failure (null / provider_error) and any unknown
// future reason show the visible unavailable state instead.
export const MARKET_HIDDEN_REASONS = new Set([
    'disabled',
    'category_not_covered',
    'make_not_covered',
    'model_not_matched',
    'insufficient_data',
]);

export const MARKET_MAX_ROWS = 15;

// Below this many USD listings the population stats stop being a market
// statement: no rail, no verdict, just the listings themselves.
export const MARKET_THIN_COUNT = 8;

// A p25/p75 dollar label within this % of a rail end would overlap the
// min/max label sitting there — drop it entirely.
export const RAIL_LABEL_EDGE_PCT = 7;

// The AI label within this % of the median label gets a vertical offset.
export const MARKER_LABEL_COLLISION_PCT = 3;

export const MARKET_UNAVAILABLE_TEXT = 'Market data is currently unavailable. Try again later.';
export const MARKET_EMPTY_TEXT = 'No comparable listings on the market right now.';
export const MARKET_FOOTNOTE = 'Advertised asking prices from public market listings, updated daily.';
export const SEPHR_NOTE = 'sep. hrs = separator hours (combine threshing time).';

// Rail labels are centred on their position — clamp the centre point 30px in
// from either edge. Delivered as a --rail-left custom property (the CSS does
// `left: var(--rail-left)`) because jsdom drops clamp() from `left`.
export const railLabelStyle = (pos) => ({ '--rail-left': `clamp(30px, ${pos}%, calc(100% - 30px))` });

// Compact dollar figure: >= $999.5k rounds into the M branch ("$1M",
// "$1.3M"), >= $1k -> "$405k", else "$850".
export function fmtCompactUsd(value) {
    if (value == null || Number.isNaN(value)) return '—';
    const abs = Math.abs(value);
    if (abs >= 999500) {
        const millions = Math.round(abs / 1e5) / 10;
        return Number.isInteger(millions) ? `$${millions}M` : `$${millions.toFixed(1)}M`;
    }
    if (abs >= 1000) return `$${Math.round(abs / 1000)}k`;
    return `$${Math.round(abs)}`;
}

// Signed compact delta of a listing's ask vs ours; "—" when either side is missing.
export function fmtAskDelta(price, ourAsk) {
    if (price == null || ourAsk == null) return '—';
    const delta = price - ourAsk;
    if (delta === 0) return fmtCompactUsd(0);
    return `${delta > 0 ? '+' : '−'}${fmtCompactUsd(delta)}`;
}

// Hour meters get no suffix, separator hours are labelled, anything unknown
// is shown lowercased so it is not silently read as engine hours.
export function meterTypeSuffix(meterType) {
    if (meterType == null) return null;
    const raw = String(meterType).trim();
    if (!raw) return null;
    const upper = raw.toUpperCase();
    if (upper === 'HOURS' || upper === 'HR') return null;
    if (upper === 'SEPHR') return 'sep. hrs';
    return raw.toLowerCase();
}

export function isSephr(meterType) {
    return String(meterType || '').trim().toUpperCase() === 'SEPHR';
}

// Case-insensitive currency read.
export function currencyOf(listing) {
    return (listing.currency || '').toUpperCase();
}

const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Recent listings read as "45d ago"; older ones as their listing month
// ("Jul 2025"); an old listing without a date degrades to "1+ yr".
export function listedLabel(listing) {
    const days = listing.days_listed;
    if (days != null && days <= 90) return `${days}d ago`;
    if (listing.listed_date) {
        // Parse the parts manually: `new Date('2025-07-01')` is UTC midnight.
        const [year, month] = String(listing.listed_date).split('-');
        const monthIndex = Number(month) - 1;
        if (/^\d{4}$/.test(year || '') && Number.isInteger(monthIndex) && monthIndex >= 0 && monthIndex <= 11) {
            return `${MONTH_ABBREVIATIONS[monthIndex]} ${year}`;
        }
    }
    if (days == null) return '—';
    return days > 365 ? '1+ yr' : `${days}d ago`;
}

export const MARKET_SORT_OPTIONS = [
    { value: 'latest', label: 'Latest' },
    { value: 'price-asc', label: 'Price ↑' },
    { value: 'price-desc', label: 'Price ↓' },
    { value: 'hours-asc', label: 'Hours ↑' },
    { value: 'hours-desc', label: 'Hours ↓' },
];

// Nulls last in EVERY mode; 'latest' keeps the served order.
export function sortMarketRows(rows, sortMode) {
    if (sortMode === 'latest') return rows;
    const field = sortMode.startsWith('price') ? 'price' : 'meter_hours';
    const direction = sortMode.endsWith('-asc') ? 1 : -1;
    return [...rows].sort((a, b) => {
        const aValue = a[field];
        const bValue = b[field];
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;
        return (aValue - bValue) * direction;
    });
}

// A blank (or unparsable) hours bound is no bound at all.
export function parseHoursBound(text) {
    const trimmed = String(text ?? '').trim();
    if (trimmed === '') return null;
    const value = Number(trimmed);
    return Number.isFinite(value) ? value : null;
}

// Linear-interpolation quantile over an ASCENDING-sorted array (numpy default).
export function quantileSorted(sorted, q) {
    if (sorted.length === 0) return null;
    const pos = (sorted.length - 1) * q;
    const lower = Math.floor(pos);
    const upper = Math.ceil(pos);
    if (lower === upper) return sorted[lower];
    return sorted[lower] + (sorted[upper] - sorted[lower]) * (pos - lower);
}

// Client-side re-statement of the server summary over the FILTERED listings'
// USD asks; percentile = share of USD asks strictly below ours.
export function filteredMarketStats(rows, ask) {
    const prices = rows
        .filter((l) => currencyOf(l) === 'USD' && typeof l.price === 'number')
        .map((l) => l.price)
        .sort((a, b) => a - b);
    if (prices.length === 0) {
        return { usdCount: 0, min: null, p25: null, median: null, p75: null, max: null, avg: null, percentile: null };
    }
    return {
        usdCount: prices.length,
        min: prices[0],
        p25: quantileSorted(prices, 0.25),
        median: quantileSorted(prices, 0.5),
        p75: quantileSorted(prices, 0.75),
        max: prices[prices.length - 1],
        avg: prices.reduce((a, b) => a + b, 0) / prices.length,
        percentile: ask != null
            ? Math.round((prices.filter((price) => price < ask).length / prices.length) * 100)
            : null,
    };
}

// Bucket key for listings without a usable state ("Unknown" in the popover).
export const STATE_UNKNOWN = '__unknown__';

export function stateKeyOf(listing) {
    const state = typeof listing.state === 'string' ? listing.state.trim() : '';
    return state || STATE_UNKNOWN;
}

// Factual position statement only — never a pricing instruction.
export function marketVerdict(ask, percentile, median) {
    if (ask == null) {
        return { pill: null, lead: 'No ask price set.', body: 'Set a price to see its market position.' };
    }
    if (percentile == null) return null;
    if (percentile <= 30) {
        const clause = median != null ? ` — ${fmtCompactUsd(median - ask)} under the median ask` : '';
        return {
            pill: 'LOW',
            lead: 'Priced low.',
            body: `Cheaper than ${100 - percentile}% of comparable listings${clause}.`,
        };
    }
    if (percentile >= 85) {
        const clause = median != null ? ` — ${fmtCompactUsd(ask - median)} over the median ask` : '';
        return {
            pill: 'HIGH',
            lead: 'Priced high.',
            body: `Above ${percentile}% of comparable listings${clause}.`,
        };
    }
    return {
        pill: 'TYPICAL',
        lead: 'Priced in the typical range.',
        body: `Within the middle of the market — ${percentile}% of listings ask less.`,
    };
}

/**
 * What the market section shows: 'loading' (in flight), 'hidden' (a known
 * no-coverage reason), 'unavailable' (null = network failure, provider_error
 * or any unknown reason), 'empty' (no listings) or 'ready'.
 */
export function marketStatus(market, loading = false) {
    if (loading || market === undefined) return 'loading';
    if (market !== null && !market.available && MARKET_HIDDEN_REASONS.has(market.reason)) return 'hidden';
    if (market === null || !market.available) return 'unavailable';
    if (!(market.listings || []).length) return 'empty';
    return 'ready';
}

/**
 * GET /market-comps on every mount, keyed by stock number and abortable
 * (P-UNIT-100). `data` is undefined while in flight, null on failure.
 */
export function useMarketComps(stockNumber) {
    const [marketState, setMarketState] = useState({ stockNumber: null, data: undefined, loading: false });
    useEffect(() => {
        if (!stockNumber) return undefined;
        const controller = new AbortController();
        let cancelled = false;
        setMarketState({ stockNumber, data: undefined, loading: true });
        apiService.getMarketComps(stockNumber, { signal: controller.signal })
            .then((data) => {
                if (!cancelled) setMarketState({ stockNumber, data, loading: false });
            })
            .catch((error) => {
                // An abort is a cleanup, never an error state.
                if (!cancelled && !isApiAbortError(error)) {
                    setMarketState({ stockNumber, data: null, loading: false });
                }
            });
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [stockNumber]);
    return {
        market: marketState.data,
        loading: marketState.loading || marketState.stockNumber !== stockNumber,
    };
}

/**
 * The Evidence Explorer's state and the view the market section renders from
 * it. Call unconditionally (rules of hooks); `view` is null unless the market
 * is ready. Mount it per unit (key by stock number) so the filters reset.
 */
export function useMarketExplorer(unit, market) {
    // null = follow the default: collapsed, except thin markets open expanded.
    const [listingsOpen, setListingsOpen] = useState(null);
    // One-way reveal past MARKET_MAX_ROWS; reset by any filter / sort change.
    const [showAll, setShowAll] = useState(false);
    const [yearsOff, setYearsOff] = useState(() => new Set());
    const [hoursMinText, setHoursMinTextRaw] = useState('');
    const [hoursMaxText, setHoursMaxTextRaw] = useState('');
    // null = "All states"; an EMPTY explicit Set is also treated as All.
    const [stateSel, setStateSel] = useState(null);
    const [statesOpen, setStatesOpen] = useState(false);
    const stateFilterRef = useRef(null);
    const stateBtnRef = useRef(null);
    const [sortMode, setSortModeRaw] = useState('latest');

    // Close the states popover on outside click or Escape, refocusing its
    // button. Escape is stopped so the layer under it (the pane, the unit
    // page) stays (D6).
    useEffect(() => {
        if (!statesOpen) return undefined;
        const closeAndRefocus = () => {
            setStatesOpen(false);
            stateBtnRef.current?.focus();
        };
        const onPointerDown = (event) => {
            if (stateFilterRef.current && !stateFilterRef.current.contains(event.target)) {
                closeAndRefocus();
            }
        };
        const onKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            event.stopPropagation();
            closeAndRefocus();
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [statesOpen]);

    const setHoursMinText = useCallback((v) => { setHoursMinTextRaw(v); setShowAll(false); }, []);
    const setHoursMaxText = useCallback((v) => { setHoursMaxTextRaw(v); setShowAll(false); }, []);
    const setSortMode = useCallback((v) => { setSortModeRaw(v); setShowAll(false); }, []);
    const selectAllStates = useCallback(() => { setStateSel(null); setShowAll(false); }, []);
    const selectNoStates = useCallback(() => { setStateSel(new Set()); setShowAll(false); }, []);
    const clearFilters = useCallback(() => {
        setYearsOff(new Set());
        setHoursMinTextRaw('');
        setHoursMaxTextRaw('');
        setStateSel(null);
        setShowAll(false);
    }, []);
    const toggleYear = useCallback((year) => {
        setYearsOff((previous) => {
            const next = new Set(previous);
            if (next.has(year)) next.delete(year);
            else next.add(year);
            return next;
        });
        setShowAll(false);
    }, []);

    const controls = {
        listingsOpen, setListingsOpen, showAll, setShowAll, yearsOff, toggleYear,
        hoursMinText, setHoursMinText, hoursMaxText, setHoursMaxText,
        stateSel, statesOpen, setStatesOpen, stateFilterRef, stateBtnRef, selectAllStates, selectNoStates,
        sortMode, setSortMode, clearFilters,
    };

    if (marketStatus(market) !== 'ready') return { ...controls, toggleState: () => {}, view: null };

    const summary = market.summary || {};
    const listings = market.listings || [];
    const yearRange = Array.isArray(summary.year_range) && summary.year_range.length === 2
        ? summary.year_range
        : null;
    const ask = typeof unit.Best_Sell_Price === 'number' && unit.Best_Sell_Price > 0
        ? unit.Best_Sell_Price
        : null;
    // AI estimate: PredictedPrice first, MLPrediction as fallback.
    const aiPriceRaw = unit.PredictedPrice || unit.MLPrediction || null;
    const aiPrice = typeof aiPriceRaw === 'number' && aiPriceRaw > 0 ? aiPriceRaw : null;

    // The ORIGINAL served index, attached before any filter / sort: the photo
    // proxy resolves photos by served-array index.
    const indexedListings = listings.map((l, i) => ({ ...l, _servedIndex: i }));

    const distinctYears = [...new Set(
        indexedListings.map((l) => l.year).filter((year) => year != null),
    )].sort((a, b) => a - b);

    const stateCounts = new Map();
    indexedListings.forEach((l) => {
        const key = stateKeyOf(l);
        stateCounts.set(key, (stateCounts.get(key) || 0) + 1);
    });
    const stateEntries = [...stateCounts.entries()]
        .map(([key, count]) => ({ key, label: key === STATE_UNKNOWN ? 'Unknown' : key, count }))
        .sort((a, b) => {
            if (a.key === STATE_UNKNOWN) return 1;
            if (b.key === STATE_UNKNOWN) return -1;
            return a.label.localeCompare(b.label);
        });
    const stateKeys = stateEntries.map((entry) => entry.key);

    const toggleState = (key) => {
        setStateSel((previous) => {
            // From the default (every state) an uncheck materialises an
            // explicit everything-but-this-state selection.
            const next = new Set(previous == null ? stateKeys : previous);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            // A selection covering every state collapses back to "All".
            return stateKeys.every((k) => next.has(k)) ? null : next;
        });
        setShowAll(false);
    };

    const hoursMin = parseHoursBound(hoursMinText);
    const hoursMax = parseHoursBound(hoursMaxText);
    const yearFilterActive = yearsOff.size > 0;
    const hoursFilterActive = hoursMin != null || hoursMax != null;
    const stateFilterActive = stateSel != null && stateSel.size > 0;
    const filtersActive = yearFilterActive || hoursFilterActive || stateFilterActive;

    const matching = sortMarketRows(indexedListings.filter((l) => {
        if (yearFilterActive && (l.year == null || yearsOff.has(l.year))) return false;
        if (hoursFilterActive) {
            if (l.meter_hours == null) return false;
            if (hoursMin != null && l.meter_hours < hoursMin) return false;
            if (hoursMax != null && l.meter_hours > hoursMax) return false;
        }
        if (stateFilterActive && !stateSel.has(stateKeyOf(l))) return false;
        return true;
    }), sortMode);

    const visible = showAll ? matching : matching.slice(0, MARKET_MAX_ROWS);
    const collapsedCount = Math.min(listings.length, MARKET_MAX_ROWS);

    const filteredStats = filtersActive ? filteredMarketStats(matching, ask) : null;
    const serverUsdCount = summary.usd_listing_count ?? 0;
    const serverStatsComplete = [
        summary.price_min_usd, summary.price_p25_usd, summary.price_median_usd,
        summary.price_p75_usd, summary.price_max_usd,
    ].every((value) => value != null);
    const serverThin = serverUsdCount < MARKET_THIN_COUNT || !serverStatsComplete;

    const usdCount = filtersActive ? filteredStats.usdCount : serverUsdCount;
    const priceMin = filtersActive ? filteredStats.min : summary.price_min_usd;
    const priceP25 = filtersActive ? filteredStats.p25 : summary.price_p25_usd;
    const priceMedian = filtersActive ? filteredStats.median : summary.price_median_usd;
    const priceP75 = filtersActive ? filteredStats.p75 : summary.price_p75_usd;
    const priceMax = filtersActive ? filteredStats.max : summary.price_max_usd;
    const percentile = filtersActive ? filteredStats.percentile : summary.subject_price_percentile;
    const avgAskUsd = filtersActive ? filteredStats.avg : summary.avg_list_price_usd;
    const statsComplete = [priceMin, priceP25, priceMedian, priceP75, priceMax]
        .every((value) => value != null);

    const thinMarket = usdCount < MARKET_THIN_COUNT || !statsComplete;
    // The default open state keys off the SERVER's thin-market call only.
    const expanded = listingsOpen == null ? serverThin : listingsOpen;

    const span = statsComplete ? priceMax - priceMin : 0;
    const showRail = !thinMarket && span > 0;
    const railPos = (value) => Math.min(100, Math.max(0, ((value - priceMin) / span) * 100));
    const p25Pos = showRail ? railPos(priceP25) : 0;
    const p75Pos = showRail ? railPos(priceP75) : 0;
    const medianPos = showRail ? railPos(priceMedian) : 0;
    const showP25Label = p25Pos >= RAIL_LABEL_EDGE_PCT && p25Pos <= 100 - RAIL_LABEL_EDGE_PCT;
    const showP75Label = p75Pos >= RAIL_LABEL_EDGE_PCT && p75Pos <= 100 - RAIL_LABEL_EDGE_PCT;
    const askRaw = showRail && ask != null ? ((ask - priceMin) / span) * 100 : null;
    const askPos = askRaw != null ? Math.min(100, Math.max(0, askRaw)) : null;
    const aiRaw = showRail && aiPrice != null ? ((aiPrice - priceMin) / span) * 100 : null;
    const aiPos = aiRaw != null ? Math.min(100, Math.max(0, aiRaw)) : null;
    const aiLabelOffset = aiPos != null
        && Math.abs(aiPos - medianPos) < MARKER_LABEL_COLLISION_PCT;
    // One tick per USD listing of the band's basis (N35), same min/max scale.
    const tickPositions = showRail
        ? (filtersActive ? matching : indexedListings)
            .filter((l) => currencyOf(l) === 'USD' && typeof l.price === 'number')
            .map((l) => railPos(l.price))
        : [];

    const verdict = thinMarket ? null : marketVerdict(ask, percentile, priceMedian);

    const hoursSource = filtersActive ? matching : listings;
    const hoursValues = hoursSource.filter((l) => l.meter_hours != null).map((l) => l.meter_hours);
    const avgHours = hoursValues.length > 0
        ? Math.round(hoursValues.reduce((a, b) => a + b, 0) / hoursValues.length)
        : null;
    const recordCount = summary.record_count ?? listings.length;

    const contextParts = [];
    if (avgAskUsd != null) contextParts.push(`avg ask ${fmtCompactUsd(avgAskUsd)}`);
    if (avgHours != null) contextParts.push(`avg ${formatNumber(avgHours)} hrs`);
    if (!filtersActive) contextParts.push(`Showing ${visible.length} of ${formatNumber(recordCount)}`);

    const subParts = [];
    if (summary.matched_model) subParts.push(summary.matched_model);
    if (yearRange) subParts.push(`${yearRange[0]}–${yearRange[1]}`);
    subParts.push('US market');

    return {
        ...controls,
        toggleState,
        view: {
            summary,
            listings,
            ask,
            aiPrice,
            distinctYears,
            stateEntries,
            stateFilterActive,
            filtersActive,
            matching,
            visible,
            collapsedCount,
            usdCount,
            thinMarket,
            expanded,
            showRail,
            priceMin,
            priceP25,
            priceMedian,
            priceP75,
            priceMax,
            p25Pos,
            p75Pos,
            medianPos,
            showP25Label,
            showP75Label,
            askRaw,
            askPos,
            aiRaw,
            aiPos,
            aiLabelOffset,
            tickPositions,
            verdict,
            avgHours,
            recordCount,
            contextParts,
            subParts,
            visibleHasSephr: visible.some((l) => isSephr(l.meter_type)),
            buildId: market.build_id,
        },
    };
}

// ── Peers and sold comps ──────────────────────────────────────────────────

/** Sold comps inside the period (0 = all time); undated rows only in All time. */
export function filterSoldComps(sold, years, now = Date.now()) {
    const rows = sold || [];
    if (years === 0) return rows;
    const cutoff = new Date(now - years * 365 * 24 * 60 * 60 * 1000);
    return rows.filter((item) => item.sold_date && new Date(item.sold_date) >= cutoff);
}

/** { count, avg, min, max } days to sell, or null without a value. */
export function soldSummary(rows) {
    const dts = (rows || []).map((s) => s.days_to_sell).filter((d) => d != null);
    if (dts.length === 0) return null;
    return {
        count: dts.length,
        avg: Math.round(dts.reduce((a, b) => a + b, 0) / dts.length),
        min: Math.min(...dts),
        max: Math.max(...dts),
    };
}

/** 'low' / 'high' / null: a peer's asking against ours. */
export function peerAskTone(peerAsk, ourAsk) {
    if (peerAsk == null || ourAsk == null) return null;
    if (peerAsk < ourAsk) return 'low';
    if (peerAsk > ourAsk) return 'high';
    return null;
}

/** Badge tone of a photo / quote count: none (0), few, good. */
export const countTone = (count, fewBelow) => {
    if (count === 0) return 'none';
    return count < fewBelow ? 'few' : 'good';
};

export const SOLD_PERIODS = [
    { value: 1, label: 'Last 1 year' },
    { value: 2, label: 'Last 2 years' },
    { value: 3, label: 'Last 3 years' },
    { value: 0, label: 'All time' },
];

// Peer list and averages without the unit itself: a pure module, so the
// Intelligence tabs can use it without the API client.
export { excludeSubject, peerSummaries } from './peerSummaries';
